"use client";

import React, { useCallback, useEffect, useRef } from "react";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { cn } from "@/lib/utils";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { useInView } from "react-intersection-observer";
import useWebview from "@/webview/useWebview";
import { isElementHidden } from "@/utils/isElementHidden";
import { type ProgramCard as ProgramCardProps } from "@/types/ProgramCard";
import { Content } from "./content";
import { appendAlgoliaQueryParamsToHref } from "@/utils/functions/appendAlgoliaQueryParamsToHref";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

export function ProgramCard(props: ProgramCardProps) {
  const {
    onClick,
    partnerName,
    name,
    href,
    location,
    tagular_variant,
    subjects,
    index = -1,
    formatType = FormatType.ProductCard,
    formatSubtype = FormatSubType.Grid,
    skipViewEvent = false,
    className,
    queryId,
    uuid,
    type,
    hasShadow = true,
  } = props;
  const { getUrl } = useWebview();
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(partnerName) ? partnerName : [partnerName ?? ""],
    ),
    formatSubtype,
    formatType,
    location: location || NYI_STRING,
    name: name,
    variant: tagular_variant || translateVariant(type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(subjects),
    productId: uuid,
  };

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    setCorrelationId(correlationId);

    productClicked();

    onClick?.(e);
  };

  const ref = useRef<HTMLAnchorElement>(undefined);

  const {
    ref: inViewRef,
    inView,
    entry,
  } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const setRefs = useCallback(
    (node: HTMLAnchorElement) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  useEffect(() => {
    if (!inView) {
      return;
    }

    if (
      !skipViewEvent &&
      entry &&
      !isElementHidden(entry.target as HTMLElement)
    ) {
      productViewed();
    }
  }, [inView]);

  // Set URL query params only if there is a query for Segment eventing
  const hrefWithQueryParams = queryId
    ? appendAlgoliaQueryParamsToHref({
        href,
        index: PRODUCT_INDEX_EN,
        queryId,
        position: (index < 0 ? NYI_NUMBER : index + 1).toString(),
      })
    : href;
  const attributedHref = useMarketplaceAttributionHref(hrefWithQueryParams);

  return (
    <a
      ref={setRefs}
      onClick={handleClick}
      className={cn("flex flex-1", className)}
      href={getUrl(attributedHref)}
    >
      <Content {...props} hasShadow={hasShadow} />
    </a>
  );
}
